import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  getSectionById,
  updateSection,
} from "../../../../../services/sectionsServices";

import { getGalleriesBySection } from "../../../../../services/gallerieServices";

import {
  buildSectionPayload,
  emptySectionForm,
  getFallbackSectionId,
  getPublicLinksCount,
  getSectionCompletion,
  isDemoMode,
  mapSectionToForm,
} from "../utils/sectionProfileUtils";

export default function useSectionProfile(sectionId) {
  const finalSectionId = useMemo(
    () => getFallbackSectionId(sectionId),
    [sectionId]
  );

  const [sectionData, setSectionData] = useState(null);
  const [formData, setFormData] = useState(emptySectionForm);
  const [galleries, setGalleries] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const demoMode = useMemo(() => isDemoMode(), []);

  const completion = useMemo(() => getSectionCompletion(formData), [formData]);
  const publicLinksCount = useMemo(
    () => getPublicLinksCount(formData),
    [formData]
  );

  const fetchData = useCallback(async () => {
    if (!finalSectionId) {
      setSectionData(null);
      setFormData(emptySectionForm);
      setGalleries([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const [section, sectionGalleries] = await Promise.all([
        getSectionById(finalSectionId),
        getGalleriesBySection(finalSectionId),
      ]);

      setSectionData(section || null);
      setFormData(mapSectionToForm(section));
      setGalleries(Array.isArray(sectionGalleries) ? sectionGalleries : []);
    } catch (error) {
      console.error("Erreur lors du chargement de la section :", error);
      toast.error("Erreur lors de la récupération des données de la section.");
    } finally {
      setLoading(false);
    }
  }, [finalSectionId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleQuillChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      about_content: value,
    }));
  };

  const handleSave = async (event) => {
    event?.preventDefault?.();

    if (demoMode) {
      toast.info("Fonction désactivée en mode Démonstration.");
      return;
    }

    if (!finalSectionId) {
      toast.error("Aucune section sélectionnée.");
      return;
    }

    if (!formData.nom_section.trim()) {
      toast.error("Le nom de la section est obligatoire.");
      return;
    }

    try {
      setSaving(true);

      const payload = buildSectionPayload(finalSectionId, formData);
      const response = await updateSection(payload);

      if (!response) {
        toast.error("Erreur lors de la mise à jour de la section.");
        return;
      }

      toast.success("Section mise à jour avec succès.");
      await fetchData();
    } catch (error) {
      console.error("Erreur lors de la mise à jour de la section :", error);
      toast.error("Erreur lors de la mise à jour de la section.");
    } finally {
      setSaving(false);
    }
  };

  return {
    finalSectionId,
    sectionData,
    formData,
    galleries,
    loading,
    saving,
    demoMode,
    completion,
    publicLinksCount,
    handleChange,
    handleQuillChange,
    handleSave,
    fetchData,
  };
}